import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton, ActionButton } from "../components/Primitives";
import { RefreshCw, Send, MapPin, CheckCircle2, XCircle, Clock3, Users2, Plus } from "lucide-react";
import { toast } from "sonner";
import { NewDispatchModal } from "../components/dispatch/NewDispatchModal";

const POLL_MS = 15000;

function relTime(iso) {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const STATUS_META = {
  en_route: { tone: "amber",  Icon: Send,         label: "En route" },
  complete: { tone: "green",  Icon: CheckCircle2, label: "Complete" },
  declined: { tone: "red",    Icon: XCircle,      label: "Declined" },
};

export default function DispatchLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dispatch/broadcasts");
      setRows(r.data || []);
      setLastFetch(new Date());
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load dispatch log"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);    return () => clearInterval(id);
  }, [load]);

  // Deep-link support: /dispatch/log?new=1 auto-opens the New Dispatch
  // modal. Used by the sidebar's dedicated "Dispatch" menu item so the
  // dispatcher lands directly in a general-broadcast composer.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowNew(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openDetail = useCallback(async (bId) => {
    if (expandedId === bId) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(bId);
    setDetailLoading(true);
    try {
      const r = await api.get(`/dispatch/broadcasts/${bId}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load broadcast detail"));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [expandedId]);

  const stats = useMemo(() => {
    const s = { total: rows.length, acked: 0, en_route: 0, complete: 0 };
    for (const r of rows) {
      const c = r.ack_counts || {};
      if ((c.total || 0) > 0) s.acked++;
      s.en_route += c.en_route || 0;
      s.complete += c.complete || 0;
    }
    return s;
  }, [rows]);

  return (
    <div data-testid="dispatch-log-page">
      <PageHeader
        title="Dispatch Log"
        subtitle="Every dispatch blast with live acknowledgement status from mobile . Auto-refreshes every 15s."
        action={
          <div className="inline-flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1" data-testid="dispatch-log-refresh-indicator">
              <RefreshCw className="size-3" /> {lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : "Loading…"}
            </div>
            <GhostButton data-testid="dispatch-log-refresh" onClick={load}>Refresh</GhostButton>
            <ActionButton data-testid="new-dispatch-button" onClick={() => setShowNew(true)}>
              <Plus className="size-4" /> New Dispatch
            </ActionButton>
          </div>
        }
      />
      <div className="p-8 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile label="Total blasts"        value={stats.total}    tone="#0A84FF" testId="dispatch-log-stat-total" />
          <StatTile label="With at least 1 ack" value={stats.acked}    tone="#8E8E93" testId="dispatch-log-stat-acked" />
          <StatTile label="En route"            value={stats.en_route} tone="#FF9F0A" testId="dispatch-log-stat-en-route" />
          <StatTile label="Complete"            value={stats.complete} tone="#34C759" testId="dispatch-log-stat-complete" />
        </div>

        {/* Table */}
        <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-sm" data-testid="dispatch-log-table">
            <thead className="border-b border-[#27272A]">
              <tr>
                <th className="px-4 py-3 text-left"><div className="label-mute">When</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Subject / Message</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Sent by</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Officers</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Ack status</div></th>
                <th className="px-4 py-3 text-right"><div className="label-mute">Detail</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {loading && <tr><td colSpan={6} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-16 text-zinc-500 scanlines">No dispatch blasts yet. Click <span className="text-[#FF3B30] font-bold">New Dispatch</span> above or send one from the Live Officers Map.</td></tr>
              )}
              {rows.map((r) => {
                const c = r.ack_counts || {};
                const targetCt = (r.guard_ids || []).length;
                const isOpen = expandedId === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr className="hover:bg-[#1c1c1e]" data-testid={`dispatch-row-${r.id}`}>
                      <td className="px-4 py-3 font-mono text-zinc-400">
                        {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{relTime(r.created_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-display font-bold uppercase tracking-tight truncate max-w-xs">{r.subject || "—"}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate max-w-xs">{r.message}</div>
                      </td>
                      <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{r.actor_email || "—"}</td>
                      <td className="px-4 py-3">
                        <PillBadge tone="default"><Users2 className="size-3 mr-1" /> {targetCt}</PillBadge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`dispatch-row-acks-${r.id}`}>
                          {c.complete > 0 && <PillBadge tone="green"><CheckCircle2 className="size-3 mr-1" />{c.complete} done</PillBadge>}
                          {c.en_route > 0 && <PillBadge tone="amber"><Send className="size-3 mr-1" />{c.en_route} en-route</PillBadge>}
                          {c.declined > 0 && <PillBadge tone="red"><XCircle className="size-3 mr-1" />{c.declined} declined</PillBadge>}
                          {targetCt - (c.total || 0) > 0 && <PillBadge tone="default"><Clock3 className="size-3 mr-1" />{targetCt - (c.total || 0)} pending</PillBadge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <GhostButton
                          data-testid={`dispatch-detail-${r.id}`}
                          onClick={() => openDetail(r.id)}
                        >{isOpen ? "Hide" : "View"}</GhostButton>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`dispatch-detail-row-${r.id}`}>
                        <td colSpan={6} className="px-4 py-4 bg-[#0a0a0a]">
                          {detailLoading && <div className="text-zinc-500 text-sm scanlines">Loading…</div>}
                          {detail && detail.broadcast?.id === r.id && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div>
                                <div className="label-mute mb-2">Message</div>
                                <div className="border border-[#27272A] p-3 text-sm text-zinc-200 whitespace-pre-wrap">{r.message}</div>
                                {r.lat != null && r.lng != null && (
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${r.lat},${r.lng}`}
                                    target="_blank" rel="noreferrer"
                                    className="mt-2 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#0A84FF] hover:underline"
                                    data-testid={`dispatch-detail-map-${r.id}`}
                                  >
                                    <MapPin className="size-3" /> Open incident location in maps
                                  </a>
                                )}
                                <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500">
                                  Channels: {(r.channels || []).join(", ") || "—"} · Push {r.push_sent}/{targetCt} · Email {r.email_sent}/{(r.email_recipients || []).length}
                                </div>
                              </div>
                              <div>
                                <div className="label-mute mb-2">Officer responses ({detail.acks.length} of {targetCt})</div>
                                <div className="border border-[#27272A] divide-y divide-[#27272A]">
                                  {detail.acks.map((a) => {
                                    const meta = STATUS_META[a.status] || { tone: "default", Icon: Clock3, label: a.status };
                                    const Icon = meta.Icon;
                                    return (
                                      <div key={a.id} className="p-3 flex items-start justify-between gap-3" data-testid={`dispatch-ack-${a.id}`}>
                                        <div className="min-w-0">
                                          <div className="font-display font-bold uppercase tracking-tight truncate">{a.guard_name || "—"}</div>
                                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{a.badge_no || "—"} · {relTime(a.updated_at || a.created_at)}</div>
                                          {a.notes && <div className="text-xs text-zinc-300 mt-1 italic">&ldquo;{a.notes}&rdquo;</div>}
                                        </div>
                                        <PillBadge tone={meta.tone}>
                                          <Icon className="size-3 mr-1" /> {meta.label}
                                        </PillBadge>
                                      </div>
                                    );
                                  })}
                                  {detail.pending.map((p) => (
                                    <div key={`pending-${p.guard_id}`} className="p-3 flex items-center justify-between gap-3 opacity-70" data-testid={`dispatch-pending-${p.guard_id}`}>
                                      <div>
                                        <div className="font-display font-bold uppercase tracking-tight">{p.guard_name || "—"}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{p.badge_no || "—"}</div>
                                      </div>
                                      <PillBadge tone="default"><Clock3 className="size-3 mr-1" /> Pending</PillBadge>
                                    </div>
                                  ))}
                                  {detail.acks.length === 0 && detail.pending.length === 0 && (
                                    <div className="p-4 text-zinc-500 text-sm">No targeted  on this broadcast.</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && (
        <NewDispatchModal
          onClose={() => setShowNew(false)}
          onSent={load}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, tone, testId }) {
  return (
    <div className="border border-[#27272A] bg-[#121212] px-4 py-3" data-testid={testId}>
      <div className="label-mute" style={{ color: tone }}>{label}</div>
      <div className="font-display text-2xl font-bold text-zinc-100 mt-1">{value}</div>
    </div>
  );
}
